/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: faxDeviceApi.ts
 🗓️ Created: 2025-12-03
 🧩 Description:
    API client functions for Fax Devices module
────────────────────────────────────────────────────────────────*/

import { buildApiUrl, getApiHeaders } from '../../../config/api';
import type { FaxDevice, FaxDeviceFormState } from '../types';

/**
 * Fetch fax devices from API
 */
export async function fetchFaxDevices(
  pagination: { page: number; limit: number; sortBy: string; sortOrder: 'asc' | 'desc' },
  search?: string,
  domainId?: number
// eslint-disable-next-line @typescript-eslint/no-explicit-any
): Promise<{ data: FaxDevice[]; pagination: any }> {
  const params = new URLSearchParams({
    page: pagination.page.toString(),
    limit: pagination.limit.toString(),
    sortBy: pagination.sortBy,
    sortOrder: pagination.sortOrder,
  });

  if (search) {
    params.append('search', search);
  }

  if (domainId != null) {
    params.append('domainId', domainId.toString());
  }

  const response = await fetch(buildApiUrl(`/api/telephony/fax-devices?${params}`), {
    method: 'GET',
    headers: getApiHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to fetch fax devices');
  }

  return response.json();
}

/**
 * Fetch single fax device by ID
 */
export async function fetchFaxDeviceById(id: string): Promise<FaxDevice> {
  const response = await fetch(buildApiUrl(`/api/telephony/fax-devices/${id}`), {
    method: 'GET',
    headers: getApiHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to fetch fax device');
  }

  const result = await response.json();
  return result.data;
}

/**
 * Create new fax device
 */
export async function createFaxDevice(data: FaxDeviceFormState): Promise<FaxDevice> {
  const response = await fetch(buildApiUrl('/api/telephony/fax-devices'), {
    method: 'POST',
    headers: getApiHeaders(),
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to create fax device');
  }

  const result = await response.json();
  return result.data;
}

/**
 * Update fax device
 */
export async function updateFaxDevice(id: string, data: Partial<FaxDeviceFormState>): Promise<FaxDevice> {
  const response = await fetch(buildApiUrl(`/api/telephony/fax-devices/${id}`), {
    method: 'PUT',
    headers: getApiHeaders(),
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to update fax device');
  }

  const result = await response.json();
  return result.data;
}

/**
 * Delete fax device
 */
export async function deleteFaxDevice(id: string): Promise<void> {
  const response = await fetch(buildApiUrl(`/api/telephony/fax-devices/${id}`), {
    method: 'DELETE',
    headers: getApiHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to delete fax device');
  }
}

